import { usePanelContentBg } from "@/theme/PanelContentBgContext";
import { useTheme } from "@/theme/ThemeProvider";
import { CaretDownIcon, CaretRightIcon } from "phosphor-react-native";
import React from "react";
import { Platform, Pressable, Text, View, ViewStyle } from "react-native";

/**
 * Sektions-Karte: flacher, ruhiger Kopf mit feiner Trennlinie (kein Gradient,
 * keine Deko) + Body. Minimalistisch und einheitlich über die ganze App.
 * headerRight: z. B. Stift/Mülleimer für Bearbeiten/Löschen.
 * collapsible: Kopf klickbar, Inhalt ein- und ausklappbar.
 */
export function SectionCard({
  title,
  subtitle,
  headerRight,
  children,
  style,
  bodyStyle,
  collapsible = false,
  expanded = true,
  onToggle,
  titleRef,
  titleAsHeader = false,
}: {
  title: string;
  subtitle?: string | null;
  /** Optionale Aktionen in der Kopfzeile (z. B. Bearbeiten, Löschen) */
  headerRight?: React.ReactNode;
  children: React.ReactNode;
  style?: ViewStyle;
  /** Optional: z. B. flex: 1, damit der Karteninhalt restlichen Platz einnimmt und ScrollView darin funktioniert */
  bodyStyle?: ViewStyle;
  /** Nur auf Profilseite: Sektion ist einklappbar */
  collapsible?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  /** Fokusziel, z. B. nach der Finder-Rückgabe. */
  titleRef?: React.Ref<Text | null>;
  titleAsHeader?: boolean;
}) {
  const { theme } = useTheme();
  const panelBg = usePanelContentBg();
  const radius = theme.radius?.panel ?? theme.radius?.lg ?? 14;
  const shadow = theme.panelShadow ?? theme.cardShadow;
  const cardBg = panelBg ?? theme.color.surface;

  const headStyle: ViewStyle = {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.space.sm,
    paddingVertical: theme.space.sm,
    paddingHorizontal: theme.space.cardPadding,
    backgroundColor: theme.color.sectionCardBodyBg,
    borderBottomWidth: 1,
    borderBottomColor: theme.color.border,
  };

  const HeadWrapper = collapsible ? Pressable : View;
  const headContent = (
    <>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          ref={titleRef}
          accessibilityRole={titleAsHeader ? "header" : undefined}
          accessible={titleAsHeader ? true : undefined}
          {...(titleAsHeader && Platform.OS === "web"
            ? ({ tabIndex: -1 } as { tabIndex: number })
            : {})}
          style={{
            color: theme.color.text,
            fontSize: theme.fontSize.small,
            fontWeight: "700",
            letterSpacing: 0.15,
          }}
          numberOfLines={1}
        >
          {title}
        </Text>
        {subtitle != null && subtitle !== "" ? (
          <Text
            style={{
              color: theme.color.muted,
              fontSize: theme.fontSize.tiny,
              fontWeight: "500",
              marginTop: 1,
            }}
            numberOfLines={1}
          >
            {subtitle}
          </Text>
        ) : null}
      </View>
      {collapsible ? (
        <View style={{ flexShrink: 0 }}>
          {expanded ? (
            <CaretDownIcon size={18} color={theme.color.muted} />
          ) : (
            <CaretRightIcon size={18} color={theme.color.muted} />
          )}
        </View>
      ) : headerRight != null ? (
        <View style={{ flexShrink: 0 }}>{headerRight}</View>
      ) : null}
    </>
  );

  return (
    <View
      style={[
        {
          borderWidth: 1,
          backgroundColor: cardBg,
          borderRadius: radius,
          borderColor: theme.color.border,
          minWidth: 0,
          maxWidth: "100%",
          overflow: Platform.OS === "web" ? ("clip" as "hidden") : "hidden",
          ...(shadow && Platform.OS !== "web" ? shadow : {}),
        },
        style,
      ]}
    >
      <HeadWrapper style={headStyle} onPress={collapsible ? onToggle : undefined}>
        {headContent}
      </HeadWrapper>
      {(!collapsible || expanded) && (
        <View
          style={[
            {
              padding: theme.space.cardPadding,
              backgroundColor: theme.color.sectionCardBodyBg,
              minWidth: 0,
              maxWidth: "100%",
            },
            bodyStyle,
          ]}
        >
          {children}
        </View>
      )}
    </View>
  );
}
